// Jakttider (Naturvårdsverket, förenklat), sol upp/ned och väderkoder.
// Utbrutet ur SatPlanerare (J1); används av planeraren, inbjudan, MarkPage och modalerna.

// ─── Sunrise/Sunset calculation ──────────────────────────────────────────
export function calcSunTimes(lat: number, lng: number, date: Date): { sunrise: string; sunset: string } {
  const rad = Math.PI / 180;
  const dayOfYear = Math.floor((date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 86400000);
  const decl = -23.45 * Math.cos(rad * 360 / 365 * (dayOfYear + 10));
  const ha = Math.acos(-Math.tan(lat * rad) * Math.tan(decl * rad)) / rad;
  const noon = 12 - lng / 15; // UTC noon
  const month = date.getMonth();
  const tzOffset = (month >= 2 && month <= 9) ? 2 : 1; // approximate CEST/CET
  const sunriseUTC = noon - ha / 15;
  const sunsetUTC = noon + ha / 15;
  const fmt = (h: number) => {
    const local = h + tzOffset;
    const hh = Math.floor(local);
    const mm = Math.round((local - hh) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  return { sunrise: fmt(sunriseUTC), sunset: fmt(sunsetUTC) };
}

// ─── WMO Weather codes ───────────────────────────────────────────────────
export const WMO_CODES: Record<number, string> = {
  0: 'Klart', 1: 'Mestadels klart', 2: 'Delvis molnigt', 3: 'Mulet',
  45: 'Dimma', 48: 'Dimma',
  51: 'Duggregn', 53: 'Duggregn', 55: 'Duggregn',
  61: 'Regn', 63: 'Regn', 65: 'Regn',
  71: 'Snöfall', 73: 'Snöfall', 75: 'Snöfall',
  80: 'Regnskurar', 81: 'Regnskurar', 82: 'Regnskurar',
  85: 'Snöbyar', 86: 'Snöbyar',
  95: 'Åskväder',
};

export interface WeatherData {
  tempMin: number;
  tempMax: number;
  precipitation: number;
  windSpeed: number;
  windDirection: number;
  weatherCode: number;
  description: string;
}

// ─── Hunting seasons (Naturvårdsverket) ───────────────────────────────────
export type SeasonEntry = { name: string; emoji: string; periods: { m1: number; d1: number; m2: number; d2: number }[]; note?: string };
export const NVV_SEASONS: SeasonEntry[] = [
  { name: 'Älg', emoji: '🫎', periods: [{ m1: 9, d1: 1, m2: 1, d2: 31 }], note: 'Varierar per region – kontrollera länsstyrelsens beslut' },
  { name: 'Dovhjort', emoji: '🦌', periods: [{ m1: 9, d1: 16, m2: 1, d2: 15 }] },
  { name: 'Kronhjort', emoji: '🦌', periods: [{ m1: 9, d1: 16, m2: 2, d2: 28 }] },
  { name: 'Vildsvin', emoji: '🐗', periods: [{ m1: 1, d1: 1, m2: 12, d2: 31 }], note: 'Året runt' },
  { name: 'Rådjur (bock)', emoji: '🦌', periods: [{ m1: 7, d1: 16, m2: 10, d2: 15 }] },
  { name: 'Rådjur (råge/kid)', emoji: '🦌', periods: [{ m1: 10, d1: 1, m2: 2, d2: 28 }] },
  { name: 'Björn', emoji: '🐻', periods: [{ m1: 8, d1: 21, m2: 10, d2: 15 }], note: 'Kvotering – tillstånd krävs' },
  { name: 'Lo', emoji: '🐆', periods: [{ m1: 3, d1: 1, m2: 3, d2: 31 }], note: 'Kvotering – tillstånd krävs' },
  { name: 'Hare', emoji: '🐇', periods: [{ m1: 11, d1: 1, m2: 1, d2: 31 }] },
  { name: 'Skogshare', emoji: '🐇', periods: [{ m1: 11, d1: 1, m2: 2, d2: 28 }] },
  { name: 'Fasan (tupp)', emoji: '🐦', periods: [{ m1: 10, d1: 1, m2: 1, d2: 31 }] },
  { name: 'Rapphöna', emoji: '🐦', periods: [{ m1: 10, d1: 1, m2: 10, d2: 31 }] },
  { name: 'Tjäder (tupp)', emoji: '🐦', periods: [{ m1: 8, d1: 25, m2: 9, d2: 30 }], note: 'Kvotering' },
  { name: 'Orre', emoji: '🐦', periods: [{ m1: 8, d1: 25, m2: 12, d2: 15 }] },
  { name: 'Järpe', emoji: '🐦', periods: [{ m1: 8, d1: 25, m2: 2, d2: 28 }] },
  { name: 'Grågås', emoji: '🦆', periods: [{ m1: 7, d1: 1, m2: 1, d2: 31 }] },
  { name: 'Kanadagås', emoji: '🦆', periods: [{ m1: 7, d1: 1, m2: 1, d2: 31 }] },
  { name: 'Kricka', emoji: '🦆', periods: [{ m1: 8, d1: 21, m2: 1, d2: 29 }] },
  { name: 'Gräsand', emoji: '🦆', periods: [{ m1: 8, d1: 21, m2: 1, d2: 29 }] },
  { name: 'Brun kärrhök (hane)', emoji: '🦅', periods: [{ m1: 9, d1: 1, m2: 1, d2: 31 }] },
  { name: 'Mink', emoji: '🦡', periods: [{ m1: 1, d1: 1, m2: 12, d2: 31 }], note: 'Året runt' },
  { name: 'Räv', emoji: '🦊', periods: [{ m1: 1, d1: 1, m2: 12, d2: 31 }], note: 'Året runt' },
  { name: 'Grävling', emoji: '🦡', periods: [{ m1: 8, d1: 1, m2: 4, d2: 30 }] },
];

export function isInSeason(season: SeasonEntry, date: Date): boolean {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  for (const p of season.periods) {
    if (p.m1 > p.m2) {
      if (m > p.m1 || m < p.m2) return true;
      if (m === p.m1 && d >= p.d1) return true;
      if (m === p.m2 && d <= p.d2) return true;
    } else if (p.m1 === p.m2) {
      if (m === p.m1 && d >= p.d1 && d <= p.d2) return true;
    } else {
      if (m > p.m1 && m < p.m2) return true;
      if (m === p.m1 && d >= p.d1) return true;
      if (m === p.m2 && d <= p.d2) return true;
    }
  }
  return false;
}

